import React, { useCallback, useContext } from 'react';
import { gql } from '@apollo/client';
import { toast } from 'react-toastify';
import Lottie from 'react-lottie-player';
import { Link } from 'react-router-dom';

import Icon from 'Components/Icon';
import AmountPaid from 'Components/AmountPaid';
import animationData from 'Shared/spiner_blue.json';
import getTotalAmountPaid from 'Shared/util/getTotalAmountPaid';
import ModalRequestQuotation from 'Components/ModalRequestQuotation';
import TabInProgressTable from './components/TabInProgressTable/TabInProgressTable';

import emptyLogo from '../../assets/QuatationsEmpty.svg';
import emptyLogoDark from '../../assets/QuatationsEmptyDark.svg';
import EmptyPage from 'Components/EmptyPage/EmptyPage';

import StoreContext from 'Shared/context/StoreContext';
import useQueryByContract from 'Shared/hooks/useQueryByContract';
import withErrorBoundary from 'Components/ErrorBoundary/ErrorBoundary';
import { logger } from 'RootShared/utility/ErrorLogger';
import useDocumentTitle from 'Shared/hooks/useDocumentTitle';

export const queryGetInProgressOrders = gql`
  query TabInProgressQuery($contractId: Int!) @api(name: root) {
    orders(status: OPEN, contractId: $contractId) {
      id
      balance
      reference
      subject
      createdAt
      invoices {
        id
        reference
      }
      total
      pdfUrl
      currency
    }
  }
`;

const TabInProgress = () => {
  const { contract } = useContext(StoreContext);
  const { loading, data, error } = useQueryByContract(contract, queryGetInProgressOrders);

  useDocumentTitle('Orders - In Progress');

  const formatingForTable = useCallback((orders) => {
    let ordersForTable = [];

    ordersForTable = orders.map(({ reference, subject, createdAt, total, balance, currency, pdfUrl, invoices, id }) => {
      return {
        order: { reference: reference, slug: subject },
        issued: { date: createdAt },
        total: { number: total, currency: currency },
        balance: { number: balance, currency: currency },
        download: { url: pdfUrl },
        invoices: invoices,
        id: id,
      };
    });

    return ordersForTable;
  }, []);

  const getOrdersComponent = useCallback(
    (data) => {
      if (data.orders.length === 0) {
        return (
          <div data-testid="tab-in-progress-empty-page">
            <EmptyPage
              img={
                <img src={JSON.parse(localStorage.getItem('nightMode')) ? emptyLogoDark : emptyLogo} alt="empty logo" />
              }
              title={'No in progress orders yet'}
              text={"Looks like you haven't had pending invoices yet."}
            />
          </div>
        );
      }

      return (
        <>
          <AmountPaid title={'Total amount'} amount={getTotalAmountPaid(data.orders)} />
          <TabInProgressTable orders={formatingForTable(data.orders)} />
        </>
      );
    },
    [formatingForTable]
  );

  if (error) {
    logger.error(error);
    toast(error.message, {
      type: toast.TYPE.ERROR,
      autoClose: 3000,
      closeButton: true,
      hideProgressBar: false,
    });
  }

  return (
    <div className="container orders" data-testid="tab-in-progress">
      <div className="tab-in-progress">
        <div className="tab-group-control-header">
          <Link
            className="tab-group-control-header__btn tab-group-control-header__btn--contained"
            to="/customer/orders/in_progress/?request_quotation"
          >
            <Icon name="icon-plus" />
            <span data-testid="request-quotation-inprogress">Request a quotation</span>
          </Link>
        </div>
        {loading && (
          <Lottie
            className="lottie"
            loop={true}
            play={true}
            animationData={animationData}
            width={100}
            style={{ width: 100, height: 100, margin: '0px auto' }}
          />
        )}
        {data && getOrdersComponent(data)}
      </div>
      <ModalRequestQuotation />
    </div>
  );
};

export default withErrorBoundary(TabInProgress);
